import React, { useEffect, useState } from 'react';
import { Table as HefestoTable, Tag } from '@gerdau/hefesto-mais-web';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import TextTooltipInformation from './TextTooltipInformation';
import { InputWrapper, ItemText, TableContainer } from './styles';
import OrderItemInputTable from './OrderItemInputTable';
import { useDispatch } from 'react-redux';
import AlertComponent from '../../../../components/AlertComponent';
import { AlertComponentType } from '../../../../components/AlertComponent/styles';

import { Icon } from '@gerdau/hefesto';
import {
  ICustomerIdParams,
  IEstimatedDateResponse
} from '../../../../store/ducks/order-simulate-checkout-mvp/types';
import { sendTagging } from '../../../../store/ducks/tagging/actions';
import useGetSenderAndReceiverDetails from './hooks/useGetSenderAndReceiverDetails';
import { purchaseOrigin } from '../../../../services/MVP/utils';
import { GetFlag } from '../../../../utils/featureFlag';

export interface CheckoutTableProps {
  orderNumber: string;
  orderItems: TableProps[];
}

export interface TableProps {
  key: number;
  itemPositionNumber?: string;
  itemName: string;
  itemQuantity: string;
  itemDesiredDate: string;
  itemDeliveryEstimate: string;
  itemDestiny: string;
  itemTaxBenefit: string;
  itemPayment: string;
  itemPrice: string;
  productId: string;
  magentoItemId?: number;
  warnings?: {
    isError: boolean;
    isEmpty: boolean;
  };
}

const ErrorText = styled.span`
  color: #b84232;
`;

const SkeletonRow = (
  <Skeleton count={1} height={'24px'} width={'100%'} style={{ margin: '0' }} />
);

const validateArray = (checkoutData: CheckoutTableProps[]) => {
  return checkoutData.map((obj) => {
    const { orderItems } = obj;

    // Rastrear itens duplicados
    const seen = new Set<string>();
    const duplicates = new Set<string>();

    // Primeiro, percorrer os itens para encontrar duplicados
    orderItems.forEach((item) => {
      if (!item.warnings) {
        item.warnings = { isEmpty: false, isError: false };
      }
      const itemPositionNumber = item.itemPositionNumber;
      if (itemPositionNumber === undefined) {
        item.warnings.isEmpty = true;
      } else {
        if (seen.has(itemPositionNumber)) {
          item.warnings.isError = true;
          duplicates.add(itemPositionNumber);
        } else {
          item.warnings.isError = false;
          item.warnings.isEmpty = false;
          seen.add(itemPositionNumber);
        }
      }
    });

    // Segundo, percorrer os itens novamente para marcar a primeira ocorrência
    const updatedOrderItems = orderItems.map((item) => {
      const itemPositionNumber = item.itemPositionNumber;
      return {
        ...item,
        warnings: {
          ...item.warnings,
          isError:
            itemPositionNumber !== undefined
              ? duplicates.has(itemPositionNumber)
              : false,
          isEmpty:
            item.warnings?.isEmpty === undefined ? false : item.warnings.isEmpty
        }
      };
    });

    return {
      ...obj,
      orderItems: updatedOrderItems
    };
  });
};

const CheckoutTable = ({
  data,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  totalPrice,
  setTableValidity,
  updateTableItem,
  estimatedDate
}: {
  data: CheckoutTableProps[];
  totalPrice: string;
  setTableValidity: (value: boolean) => void;
  updateTableItem: (param: ICustomerIdParams) => void;
  estimatedDate: {
    loading: boolean;
    error: boolean;
    data: IEstimatedDateResponse | undefined;
  };
}) => {
  const [dataState, setDataState] = useState(data?.length ? data : []);
  const dispatch = useDispatch();
  const { emitterSelected, receiverSelected } =
    useGetSenderAndReceiverDetails();

  const newCheckoutFlow = GetFlag('new_checkout_flow');

  useEffect(() => {
    const checkoutData = validateArray(data);
    setDataState(checkoutData);
  }, [data]);

  useEffect(() => {
    const hasError = dataState.some((table) =>
      table.orderItems.some((item) => item.warnings?.isError)
    );
    setTableValidity(!hasError);
  }, [dataState]);

  const setItemPositionNumber = (
    itemKey: TableProps,
    orderNumber: string,
    value?: string
  ) => {
    const customerIdParams: ICustomerIdParams = {
      customerId: value ?? '',
      itemId: itemKey.productId,
      index: itemKey.key,
      salesOrderId: orderNumber
    };
    updateTableItem(customerIdParams);
  };

  const hasFiscalBenefit = () => {
    return dataState.some((order) =>
      order.orderItems.some((item) => item.itemTaxBenefit === 'X')
    );
  };

  const getColumns = (orderNumber: string) => {
    return [
      {
        title: 'Nº do item',
        dataIndex: 'itemPositionNumber',
        render: (item: TableProps, record: TableProps) => {
          return (
            <InputWrapper table>
              <OrderItemInputTable
                maxLengthInput={10}
                orderItemInput={record.itemPositionNumber}
                setOrderItemInput={(value) =>
                  setItemPositionNumber(record, orderNumber, value)
                }
                warnings={record?.warnings}
              ></OrderItemInputTable>
            </InputWrapper>
          );
        }
      },
      {
        title: 'Produto',
        dataIndex: 'itemName',
        render: (text: string) => (
          <div style={{ minWidth: '200px' }}>{text}</div>
        )
      },
      {
        title: 'Quantidade',
        dataIndex: 'itemQuantity',
        render: (text: string) => <>{text}</>
      },
      {
        title: 'Data desejada',
        dataIndex: 'itemDesiredDate',
        render: (text: string | React.JSX.Element) => (
          <>{typeof text === 'string' ? text.replace(/-/g, '/') : text}</>
        )
      },
      {
        title: (
          <TextTooltipInformation
            title="Em casos de mais de uma entrega para o material, a data exibida refere-se a data estimada da primeira entrega. 
A estimativa pode sofrer oscilações"
          >
            <span>Estimativa de entrega</span>
          </TextTooltipInformation>
        ),
        dataIndex: 'itemDeliveryEstimate',
        render: (item: TableProps, record: TableProps) => (
          <>
            {(() => {
              if (estimatedDate.loading && newCheckoutFlow) {
                return SkeletonRow;
              }
              if (typeof record.itemDeliveryEstimate === 'string') {
                if (estimatedDate.error && newCheckoutFlow) {
                  return <ErrorText>Erro ao carregar os dados</ErrorText>;
                }
                if (record.itemDeliveryEstimate.length > 0) {
                  return record.itemDeliveryEstimate.replace(/-/g, '/');
                }
                return <Tag variant="warning">Sem estimativa</Tag>;
              }

              if (newCheckoutFlow) {
                return <ErrorText>Erro ao carregar os dados</ErrorText>;
              }

              return <Tag variant="warning">Sem estimativa</Tag>;
            })()}
          </>
        )
      },
      {
        title: 'Destinação',
        dataIndex: 'itemDestiny',
        render: (text: string) => <>{text}</>
      },
      ...(hasFiscalBenefit()
        ? [
            {
              title: 'Benefício fiscal',
              dataIndex: 'itemTaxBenefit',
              render: (text: string) => (
                <>{text && text.toLowerCase() === 'x' ? 'Sim' : 'Não'}</>
              )
            }
          ]
        : []),
      ...(newCheckoutFlow
        ? []
        : [
            {
              title: 'Pagamento',
              dataIndex: 'itemPayment',
              render: (text: string) => <>{text}</>
            }
          ]),
      {
        title: 'Preço',
        dataIndex: 'itemPrice',
        render: (text: string) => <>{text}</>
      }
    ];
  };

  function handleTagError(eventName: string) {
    const tipo_compra = purchaseOrigin();
    dispatch(
      sendTagging({
        event: eventName,
        screenName: 'revisar_pedido',
        tipo_compra: tipo_compra,
        bp_emissor: emitterSelected?.customerId,
        bp_recebedor: receiverSelected?.customerId
      })
    );
  }

  return (
    <TableContainer>
      {dataState.map((order, i) => (
        <div key={order.orderNumber}>
          {!newCheckoutFlow && (
            <ItemText isTitle>Ordem de venda nº{i + 1}</ItemText>
          )}
          {order.orderItems.find((item) => item.warnings?.isEmpty) && (
            <AlertComponent
              message="Preencha os campos nº do item."
              type={AlertComponentType.warning}
            >
              {handleTagError('num_item_vazio')}
              <Icon option="InfoCircle" variant="warning" size="sm" />
            </AlertComponent>
          )}
          {order.orderItems.find((item) => item.warnings?.isError) && (
            <AlertComponent
              message="Os itens não podem ter números iguais."
              type={AlertComponentType.danger}
            >
              {handleTagError('erro_itens_numeros_iguais')}
              <Icon option="CancelCircle" variant="danger" size="sm" />
            </AlertComponent>
          )}
          <HefestoTable
            dataSource={order.orderItems}
            columns={getColumns(order.orderNumber)}
          />
        </div>
      ))}
    </TableContainer>
  );
};

export default CheckoutTable;
